import { type FormEvent, useState } from "react";
import type { Account, PortalApi } from "../api.ts";
import type { Identity, Role } from "../auth.ts";
import { messageOf, useLoad } from "../load.ts";
import { Badge, Button, Field, Input, Notice, PageLayout, Select, Table, Toolbar } from "../ui.tsx";

/**
 * Accounts (docs/security.md#accounts): admins create members and disable or enable them;
 * operators create any role, change roles and disable any account. The data API enforces the same
 * rule and says which accounts the caller may change (`manageable`); the page offers only those.
 */

const ALL_ROLES: readonly Role[] = ["member", "admin", "operator"];

/** The roles a caller may give: an admin members only. */
export function grantableRoles(role: Role | undefined): readonly Role[] {
  if (role === "operator") return ALL_ROLES;
  if (role === "admin") return ["member"];
  return [];
}

export function AccountsPage({ api, identity }: { api: PortalApi; identity: Identity }) {
  const list = useLoad(() => api.accounts(), [api]);
  const [notice, setNotice] = useState<{ text: string; error?: boolean }>();
  const grantable = grantableRoles(identity.role);

  /** Puts a changed account in the list in place of its old row. */
  const replace = (account: Account) => {
    const current = list.data ?? { accounts: [], truncated: false };
    const others = current.accounts.filter((other) => other.email !== account.email);
    list.set({
      ...current,
      accounts: [...others, account].sort((a, b) => a.email.localeCompare(b.email)),
    });
  };

  const run = async (change: () => Promise<Account>, done: (account: Account) => string) => {
    setNotice(undefined);
    try {
      const account = await change();
      replace(account);
      setNotice({ text: done(account) });
      return true;
    } catch (error) {
      setNotice({ text: messageOf(error), error: true });
      return false;
    }
  };

  return (
    <PageLayout title="Accounts">
      <CreateForm
        roles={grantable}
        onCreate={(email, role) =>
          run(
            () => api.createAccount(email, role),
            (account) =>
              `Created ${account.email} as ${role}. They sign in to the Quote Agent app with this email and the code sent to it.`,
          )
        }
      />
      {notice && (
        <Notice tone={notice.error ? "bad" : "ok"} role="status">
          {notice.text}
        </Notice>
      )}
      {list.loading && <p className="text-muted">Loading…</p>}
      {list.error && <Notice tone="bad">{list.error}</Notice>}
      {list.data && (
        <>
          {list.data.truncated && (
            <Notice tone="bad">There are more accounts than are listed here.</Notice>
          )}
          <Table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Enabled</th>
                <th>Status</th>
                <th>Created</th>
                <th>Change</th>
              </tr>
            </thead>
            <tbody>
              {list.data.accounts.map((account) => (
                <AccountRow
                  key={account.email}
                  account={account}
                  roles={grantable}
                  onRole={(role) =>
                    run(
                      () => api.setAccountRole(account.email, role),
                      (changed) =>
                        `${changed.email} is now ${role}. It takes effect when their app next renews its sign-in, within the hour.`,
                    )
                  }
                  onEnabled={(enabled) =>
                    run(
                      () => api.setAccountEnabled(account.email, enabled),
                      (changed) =>
                        enabled
                          ? `Enabled ${changed.email}; it can sign in again.`
                          : `Disabled ${changed.email}; it can no longer sign in.`,
                    )
                  }
                />
              ))}
            </tbody>
          </Table>
        </>
      )}
    </PageLayout>
  );
}

function CreateForm({
  roles,
  onCreate,
}: {
  roles: readonly Role[];
  onCreate: (email: string, role: Role) => Promise<boolean>;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>("member");
  const [busy, setBusy] = useState(false);
  if (roles.length === 0) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    if (await onCreate(email.trim(), role)) setEmail("");
    setBusy(false);
  };

  return (
    <Toolbar onSubmit={submit} aria-label="Create an account">
      <Field label="Email">
        <Input
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="producer@agency.com"
          className="w-64 max-w-full"
        />
      </Field>
      {roles.length > 1 ? (
        <Field label="Role">
          <Select value={role} onChange={(event) => setRole(event.target.value as Role)}>
            {roles.map((candidate) => (
              <option key={candidate} value={candidate}>
                {candidate}
              </option>
            ))}
          </Select>
        </Field>
      ) : (
        <span className="text-muted text-ui">as a {roles[0]}</span>
      )}
      <Button type="submit" variant="primary" disabled={busy || email.trim() === ""}>
        Create account
      </Button>
    </Toolbar>
  );
}

function AccountRow({
  account,
  roles,
  onRole,
  onEnabled,
}: {
  account: Account;
  roles: readonly Role[];
  onRole: (role: Role) => Promise<boolean>;
  onEnabled: (enabled: boolean) => Promise<boolean>;
}) {
  const [role, setRole] = useState<Role>(account.role ?? "member");
  const [busy, setBusy] = useState(false);
  const act = async (change: () => Promise<boolean>) => {
    setBusy(true);
    await change();
    setBusy(false);
  };
  const roleText =
    account.roles.length === 0
      ? "none"
      : account.roles.length > 1
        ? `${account.roles.join(" + ")} (set one)`
        : account.role;
  // Changing a role means something only where there is a choice, or the account has no single role.
  const canSetRole =
    account.manageable && (roles.length > 1 || (roles.length === 1 && account.roles.length !== 1));

  return (
    <tr>
      <td>{account.email}</td>
      <td>{roleText}</td>
      <td>
        <Badge tone={account.enabled ? "ok" : "neutral"}>{account.enabled ? "yes" : "no"}</Badge>
      </td>
      <td>{account.status}</td>
      <td>{account.createdAt?.slice(0, 10) ?? "—"}</td>
      <td className="space-x-1.5">
        {!account.manageable && <span className="text-muted">—</span>}
        {canSetRole && (
          <>
            {roles.length > 1 && (
              <Select
                compact
                aria-label={`Role of ${account.email}`}
                value={role}
                onChange={(event) => setRole(event.target.value as Role)}
              >
                {roles.map((candidate) => (
                  <option key={candidate} value={candidate}>
                    {candidate}
                  </option>
                ))}
              </Select>
            )}
            <Button
              type="button"
              size="sm"
              disabled={busy || (account.roles.length === 1 && role === account.role)}
              onClick={() => act(() => onRole(roles.length > 1 ? role : (roles[0] as Role)))}
            >
              {roles.length > 1 ? "Set role" : `Make ${roles[0]}`}
            </Button>
          </>
        )}
        {account.manageable && (
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={() => act(() => onEnabled(!account.enabled))}
          >
            {account.enabled ? "Disable" : "Enable"}
          </Button>
        )}
      </td>
    </tr>
  );
}
